// src/utils/notifications.js

/**
 * notifications helper
 *
 * - playNotificationSound(): attempts to play the global notification sound
 *   (expects the file at /sounds/notification.mp3 in the public folder).
 *
 * - showBrowserNotification(title, options): shows a native notification,
 *   requesting permission if needed (but only when permission is "default").
 *
 * - requestNotificationPermission(): friendly wrapper to request permission once
 *   (resolves to the permission string: "granted" | "denied" | "default").
 *
 * NOTE: Browsers may block audio autoplay if the user hasn't interacted with the page.
 * The showBrowserNotification() is the reliable fallback for background/inactive tabs.
 */

export function playNotificationSound() {
  try {
    const audio = new Audio('/sounds/notification.mp3');

    // Try to play and return the promise so callers can handle it if desired
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        // Autoplay blocked or playback error — we log and continue
        console.warn('Notification sound play blocked or failed:', err);
      });
    }

    return playPromise;
  } catch (err) {
    console.error('Error initializing notification sound:', err);
    // Keep API consistent: return a rejected Promise
    return Promise.reject(err);
  }
}

export function showBrowserNotification(title, options = {}) {
  if (!('Notification' in window)) {
    console.warn('Browser does not support Notification API.');
    return;
  }

  // If permission already granted, show immediately
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, options);
    } catch (err) {
      console.error('Failed to show browser notification:', err);
    }
    return;
  }

  // If permission explicitly denied, don't ask again programmatically
  if (Notification.permission === 'denied') {
    console.warn('Notification permission has been denied by the user.');
    return;
  }

  // permission === 'default' -> ask the user (only when needed)
  Notification.requestPermission().then((permission) => {
    if (permission === 'granted') {
      try {
        new Notification(title, options);
      } catch (err) {
        console.error('Failed to show browser notification after granting permission:', err);
      }
    } else {
      // permission === 'denied' or 'default'
      console.warn('Notification permission result:', permission);
    }
  }).catch((err) => {
    console.error('Error requesting notification permission:', err);
  });
}

export function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('Browser does not support Notification API.');
    return Promise.resolve('unsupported');
  }

  // Only prompt if user hasn't decided yet
  if (Notification.permission === 'default') {
    return Notification.requestPermission();
  }

  // Return current status as a resolved promise for uniform use
  return Promise.resolve(Notification.permission);
}
